import React, { useEffect, useState } from "react";
import ModalWrapper from "../../../app/common/modals/ModalWrapper";
import { getJobType } from "../helpers/jobTypes";
import { getJobIndustry } from "../helpers/jobIndustries";
import { Grid, Image, Label, Modal, Segment } from "semantic-ui-react";
import { SEMANTIC_DIV } from "../../../css/styles";
import getDateDurationSinceDate from "../../../js/helpers/getDateDurationSinceDate";
import { useDispatch, useSelector } from "react-redux";
import { Link, Redirect } from "react-router-dom";
import useFirestoreDoc from "../../../app/hooks/useFirestoreDoc";
import { listenToDocFromFirestore } from "../../../app/firestore/firestoreService";
import { openModal } from "../../../app/common/modals/modalReducer";
import { JOB_UNAVAILABLE_MODAL } from "../../../app/common/modals/ModalManager";
import SaveIconButton from "../../../app/common/components/SaveIconButton";
import { updateViewCount } from "../../../app/firestore/helpers/updateViewCount";
import ReactGA from "react-ga4";
import { isJobFeatured, isJobSponsored } from "../helpers/checkJobPromotions";
import { isItemExpired } from "../helpers/compareJobDates";
import { getJobExpiringStatus } from "../../../js/helpers/getDaysBetweenTwoDates";
import isEqual from "lodash/isEqual";
import filter from "lodash/filter";
import {
  FIRESTORE_JOBS_COLLECTION,
  FIRESTORE_VIEWS_COLLECTION,
  QUERY_CODES,
} from "../../../app/firestore/firestoreConstants";
import { asyncActionFinish } from "../../../app/async/asyncReducer";
import RecruiterTag from "../../employer/components/RecruiterTag";
import Tags from "../../../app/common/components/Tags";
import JobContactDetails from "./JobContactDetails";
import { getHealthcareCategory } from "../../modes/healthcare/jobHealthcareCategories";
import { HR_ALL_MODE, HR_HEALTHCARE_MODE } from "../../modes/constants";

export default function JobDetailsModal({ job }) {
  const dispatch = useDispatch();

  const { currentUserAccount } = useSelector((state) => state.account);
  const { loading, error } = useSelector((state) => state.async);
  const { isCookieConsent, visitorId, responsive } = useSelector(
    (state) => state.app
  );

  const [selectedJob, setSelectedJob] = useState(null);
  const [isViewed, setIsViewed] = useState(false);
  const [viewCountForJob, setViewCountForJob] = useState(null);

  useFirestoreDoc({
    shouldExecute: job.id && job.refNumber !== "",
    query: () =>
      listenToDocFromFirestore({
        dbCollection: FIRESTORE_JOBS_COLLECTION,
        id: job.id,
      }),
    data: (selectedJob) => setSelectedJob(selectedJob),
    deps: [job, dispatch],
    queryCode: QUERY_CODES.listenToJobFromFirestore,
  });

  useFirestoreDoc({
    shouldExecute: selectedJob && selectedJob.isInitialised,
    query: () =>
      listenToDocFromFirestore({
        dbCollection: FIRESTORE_VIEWS_COLLECTION,
        id: selectedJob.refNumber,
      }),
    data: (viewCount) => setViewCountForJob(viewCount),
    deps: [selectedJob, currentUserAccount, dispatch],
  });

  useEffect(() => {
    if (job.dateCreated === "" || !selectedJob) return;
    if (!isEqual(job, selectedJob)) {
      dispatch(asyncActionFinish());
      dispatch(openModal({ modalType: JOB_UNAVAILABLE_MODAL }));
    }
  }, [dispatch, job, selectedJob]);

  useEffect(() => {
    if (
      !viewCountForJob ||
      currentUserAccount?.id === job.employerId ||
      currentUserAccount?.isAdmin ||
      isViewed
    )
      return;

    if (
      filter(viewCountForJob.views, (view) => view.viewerId === visitorId)
        .length === 0
    ) {
      updateViewCount({
        isViewed: isViewed,
        jobRefNumber: job.refNumber,
        viewerId: visitorId,
      }).then(() => setIsViewed(true));
    }
  }, [visitorId, currentUserAccount, viewCountForJob, isViewed, job]);

  if (error) return <Redirect to="/error" />;

  const viewFullScreenButton = job?.refNumber !== "" &&
    job?.isApproved &&
    !isItemExpired(job) && (
      <Segment
        basic
        style={{ ...SEMANTIC_DIV }}
        as={Link}
        to={`/jobs/${job.refNumber}`}
        rel="noopener noreferrer"
        target="_blank"
        onClick={() => {
          if (isCookieConsent) {
            ReactGA.event({
              category: "Button",
              action: "View job on fullscreen",
            });
          }
        }}
      >
        <span className="button-text text-link">Fullscreen</span>
      </Segment>
    );

  return (
    <ModalWrapper
      style={{ width: !responsive.isMobile ? 850 : "99%", padding: 0 }}
      modalTopButton={!responsive.isMobile && viewFullScreenButton}
      modalClassName="modal-ux-wrapper"
      onMount={() => {
        if (isCookieConsent) {
          ReactGA.event({
            category: "Views",
            action: "Job details modal",
            label: job.refNumber,
          });
        }
      }}
    >
      <Segment basic style={{ ...SEMANTIC_DIV }} disabled={loading}>
        <Grid className="job-details-modal-container" stackable>
          {responsive.isMobile && (
            <Grid.Row centered>{viewFullScreenButton}</Grid.Row>
          )}
          {job.mode === HR_HEALTHCARE_MODE && (
            <div className="ui ribbon label healthcare-label">
              <span className={"ribbon-text"}>Hello Radius Healthcare</span>
            </div>
          )}
          <div>
            {!job.isInitialised && (
              <Label color="blue">
                <span className={"ribbon-text"}>
                  {responsive.isMobile
                    ? "Preview | Dates not shown"
                    : "Preview | Dates (posted/expires) will show when your job is published"}
                </span>
              </Label>
            )}
          </div>
          <Grid.Row>
            <Grid.Column width={!responsive.isMobile ? 11 : 15}>
              {job.isPostedByRecruiter && responsive.isMobile && (
                <RecruiterTag
                  inline={false}
                  style={{ margin: "8px 70px 8px 0" }}
                />
              )}
              <div style={{ marginBottom: 7, paddingRight: "10%" }}>
                {isJobFeatured(job) && job.isInitialised && (
                  <div className="featured-promotion-item">Featured</div>
                )}
                {isJobSponsored(job) && job.isInitialised && (
                  <div className="sponsored-promotion-item">Sponsored</div>
                )}
                <h2 style={{ marginTop: 8 }}>{job.title}</h2>
              </div>
              <div data-testid="job-details-modal-company-exists-testId">
                <span>{job.company}</span>
              </div>
              <div className="details-item" style={{ marginTop: 22 }}>
                <span className="body-heavy-text">Job type: </span>
                <span>{getJobType(job.type)}</span>
              </div>
              {/* {job.salary.type !== BLANK_VALUE && job.salary.type !== "" && (
                <div className="details-item">
                  <span className="body-heavy-text">Salary: </span>
                  <span>{job.salary.display}</span>
                </div>
              )} */}
              {job.mode === HR_ALL_MODE && (
                <div className="details-item">
                  <span className="body-heavy-text">Industry: </span>
                  <span>{getJobIndustry(job.industry)}</span>
                </div>
              )}
              {job.mode === HR_HEALTHCARE_MODE && (
                <div className="details-item">
                  <span className="body-heavy-text">Category: </span>
                  <span>{getHealthcareCategory(job.category)}</span>
                </div>
              )}
              {job.isInitialised && (
                <div className="details-item">
                  <span className="body-heavy-text">Posted: </span>
                  <span>{getDateDurationSinceDate(job.datePublished)}</span>
                </div>
              )}
            </Grid.Column>
            {((job.isPostedByRecruiter && !responsive.isMobile) ||
              job.companyLogoPhotoURL) && (
              <Grid.Column
                width={5}
                style={{ padding: "40px 20px 0 0" }}
                textAlign="right"
              >
                {job.isPostedByRecruiter && !responsive.isMobile && (
                  <RecruiterTag />
                )}
                {job.companyLogoPhotoURL && (
                  <Image
                    src={job.companyLogoPhotoURL}
                    style={{ width: "100%", height: "100%" }}
                  />
                )}
              </Grid.Column>
            )}
          </Grid.Row>
          <Grid.Row style={{ paddingTop: 0 }}>
            <Grid.Column
              width={16}
              verticalAlign="middle"
              style={{ fontSize: 15 }}
              floated="left"
            >
              {job.isInitialised && (
                <span className="bold-text">
                  {getJobExpiringStatus({
                    date: job.dateExpiring,
                    show: job.isInitialised === true,
                  })}
                </span>
              )}
              {job.dateCreated !== "" &&
                job.refNumber !== "" &&
                job.isApproved &&
                !currentUserAccount?.isEmployer && (
                  <SaveIconButton
                    isJobSeeker={currentUserAccount?.isJobSeeker}
                    userId={currentUserAccount?.id}
                    job={job}
                    floated={"right"}
                    isCloseModal={true}
                  />
                )}
            </Grid.Column>
          </Grid.Row>
          <Grid.Row style={{ padding: 0 }}>
            <Grid.Column width={16}>
              <div className="modal-content-job-description-container">
                <Modal.Content
                  scrolling
                  style={{ minHeight: 100, maxHeight: 250 }}
                >
                  <div className="body-heavy-text underline-text job-description-title">
                    Job Description
                  </div>
                  <div className="details-item">
                    <div
                      className="rich-text-display-block"
                      dangerouslySetInnerHTML={{
                        __html: job.description,
                      }}
                    />
                  </div>
                </Modal.Content>
              </div>
            </Grid.Column>
          </Grid.Row>
          {job.skillTags?.length > 0 && (
            <Grid.Row style={{ paddingBottom: 0 }}>
              <Grid.Column computer={15}>
                <Tags tags={job.skillTags} />
              </Grid.Column>
            </Grid.Row>
          )}
          <Grid.Row>
            <Grid.Column width={9}>
              <JobContactDetails job={job} />
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Segment>
    </ModalWrapper>
  );
}
